import { type PWAInstallElement } from "@khmyznikov/pwa-install";
import PWAInstall from "@khmyznikov/pwa-install/react-legacy";
import { useRef } from "react";

import StorageManager from "@/features/storage";

const PWA_PROMPT_THRESHOLD_DAYS = 2;

/** The library dispatches new CustomEvent(name, { detail: { message } }). */
type UserChoiceResultEvent = CustomEvent<{ message: string }>;

interface PWAInstallComponentProps {
  onInstallSuccess?: (event: Event) => void;
  onInstallFail?: (event: Event) => void;
  onUserChoiceResult?: (event: Event) => void;
  onInstallAvailable?: (event: Event) => void;
  onInstallHowTo?: (event: Event) => void;
  onInstallGallery?: (event: Event) => void;
  manifestUrl?: string;
  icon?: string;
  name?: string;
  description?: string;
  installDescription?: string;
  disableDescription?: boolean;
  disableScreenshots?: boolean;
  manualApple?: boolean;
  manualChrome?: boolean;
  disableChrome?: boolean;
}

export const PWAInstallComponent = ({
  onInstallSuccess,
  onInstallFail,
  onUserChoiceResult,
  onInstallAvailable,
  onInstallHowTo,
  onInstallGallery,
  ...props
}: PWAInstallComponentProps) => {
  const pwaInstallRef = useRef<PWAInstallElement>(null);

  const nonNullProps = Object.fromEntries(
    Object.entries(props).filter(([, value]) => value != null)
  );

  const handleInstallAvailable = (event: Event) => {
    if (shouldShowPWAInstallPrompt()) {
      pwaInstallRef.current?.showDialog(true);
    }

    onInstallAvailable?.(event);
  };
  const handleInstallSuccess = (event: Event) => onInstallSuccess?.(event);
  const handleInstallFail = (event: Event) => onInstallFail?.(event);
  const handleInstallGallery = (event: Event) => onInstallGallery?.(event);
  const handleInstallHowTo = (event: Event) => {
    if (shouldShowPWAInstallPrompt()) {
      onInstallHowTo?.(event);
      StorageManager.setPWAInstructionsShownDate();
    }
  };
  const handleUserChoiceResult = (event: Event) => {
    const { detail } = event as UserChoiceResultEvent;
    if (detail.message === "dismissed") {
      StorageManager.setDismissPWAInstallDate();
    }

    onUserChoiceResult?.(event);
  };

  const shouldShowPWAInstallPrompt = (): boolean => {
    const instructionsDate = StorageManager.getPWAInstructionsShownDate();

    if (instructionsDate) {
      return false;
    }

    const dismissDate = StorageManager.getDismissPWAInstallDate();

    if (!dismissDate) {
      return true;
    }

    const currentDate = new Date();
    const thresholdDate = new Date(dismissDate);

    thresholdDate.setDate(thresholdDate.getDate() + PWA_PROMPT_THRESHOLD_DAYS);

    return currentDate >= thresholdDate;
  };

  return (
    <PWAInstall
      ref={pwaInstallRef}
      onPwaInstallAvailableEvent={handleInstallAvailable}
      onPwaInstallSuccessEvent={handleInstallSuccess}
      onPwaInstallFailEvent={handleInstallFail}
      onPwaInstallGalleryEvent={handleInstallGallery}
      onPwaUserChoiceResultEvent={handleUserChoiceResult}
      onPwaInstallHowToEvent={handleInstallHowTo}
      {...nonNullProps}
    />
  );
};
